import type { OrganizationCustomRoleKey } from "@clerk/expo/types";
import {
  AlertTriangle,
  Building2,
  Check,
  CreditCard,
  Crown,
  Info,
  KeyRound,
  Lock,
  LucideIcon,
  OctagonAlert,
  ShieldCheck,
} from "lucide-react-native";
import { FC, ReactNode, useMemo } from "react";
import { Image, KeyboardTypeOptions, Switch } from "react-native";

import {
  Box,
  HStack,
  Input,
  InputField,
  Pressable,
  Text,
  Tooltip,
  TooltipContent,
  TooltipText,
  VStack,
} from "@/components/ui";
import { useLocalization, useRegionLabels } from "@/hooks";
import { Region } from "@/types/resources";

import { AccountStatus } from "./utils";

const AVATAR_COLORS = [
  "#7c3aed",
  "#2563eb",
  "#059669",
  "#d97706",
  "#dc2626",
  "#0891b2",
  "#db2777",
];

const AVATAR_SIZES = { lg: 40, md: 32, sm: 28 };

const CALLOUT_STYLE = {
  danger: { bg: "#fef2f2", border: "#fecaca", color: "#b91c1c" },
  info: { bg: "#f5f3ff", border: "#ddd6fe", color: "#6d28d9" },
  warning: { bg: "#fffbeb", border: "#fde68a", color: "#b45309" },
};

const STATUS_STYLE: Record<AccountStatus, { bg: string; color: string }> = {
  active: { bg: "#ecfdf5", color: "#059669" },
  offboarded: { bg: "#f1f5f9", color: "#64748b" },
};

// =============================================================================
// Display
// =============================================================================
export const AccountAvatar: FC<{
  id: string;
  imageUrl?: string | null;
  kind: "org" | "user";
  name: string;
  size?: keyof typeof AVATAR_SIZES;
}> = ({ id, imageUrl, kind, name, size = "md" }) => {
  const px = AVATAR_SIZES[size];
  const hash = id.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const initials =
    name
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0] ?? "")
      .join("")
      .toUpperCase() || "?";

  return (
    <Box
      className="rounded-xl items-center justify-center shrink-0 overflow-hidden"
      style={{
        backgroundColor: imageUrl
          ? "transparent"
          : kind === "org"
            ? "#e2e8f0"
            : AVATAR_COLORS[hash % AVATAR_COLORS.length],
        height: px,
        width: px,
      }}
    >
      {imageUrl ? (
        <Image source={{ uri: imageUrl }} style={{ height: px, width: px }} />
      ) : kind === "org" ? (
        <Building2 color="#475569" size={px / 2.2} />
      ) : (
        <Text bold className="text-white text-xs">
          {initials}
        </Text>
      )}
    </Box>
  );
};

export const Callout: FC<{
  children: ReactNode;
  title?: string;
  tone?: keyof typeof CALLOUT_STYLE;
}> = ({ children, title, tone = "info" }) => {
  const style = CALLOUT_STYLE[tone];
  const Icon =
    tone === "danger"
      ? OctagonAlert
      : tone === "warning"
        ? AlertTriangle
        : Info;
  return (
    <HStack
      className="gap-2.5 rounded-xl border p-3"
      style={{ backgroundColor: style.bg, borderColor: style.border }}
    >
      <Box className="pt-0.5">
        <Icon color={style.color} size={14} />
      </Box>
      <VStack className="flex-1 gap-0.5">
        {title && (
          <Text bold className="text-sm" style={{ color: style.color }}>
            {title}
          </Text>
        )}
        <Text className="text-xs leading-5" style={{ color: style.color }}>
          {children}
        </Text>
      </VStack>
    </HStack>
  );
};

export const RegionBadge: FC<{ region: Region }> = ({ region }) => {
  const labels = useRegionLabels();
  return (
    <HStack className="items-center gap-1 rounded-full bg-background-50 border border-outline-100 px-2 py-0.5">
      <Image
        source={{ uri: labels[region].flagUrl }}
        style={{ borderRadius: 2, height: 9, width: 13 }}
      />
      <Text className="text-[11px] font-medium text-typography-500">
        {labels[region].short}
      </Text>
    </HStack>
  );
};

export const RoleBadge: FC<{
  isOwner?: boolean;
  role: OrganizationCustomRoleKey | null;
}> = ({ isOwner, role }) => {
  const labels = useRoleLabels();
  if (!role || (!isOwner && role !== "org:admin")) return null;
  const label = isOwner ? labels.owner : labels["org:admin"];
  const { color, Icon } = isOwner
    ? { color: "#7c3aed", Icon: Crown }
    : { color: "#6d28d9", Icon: ShieldCheck };
  return (
    <Tooltip
      placement="top"
      trigger={(triggerProps) => (
        <Pressable {...triggerProps}>
          <Icon color={color} size={14} />
        </Pressable>
      )}
    >
      <TooltipContent>
        <TooltipText>{label}</TooltipText>
      </TooltipContent>
    </Tooltip>
  );
};

export const SectionTitle: FC<{ subtitle?: string; title: string }> = ({
  subtitle,
  title,
}) => (
  <VStack className="gap-0.5">
    <Text bold className="text-sm text-typography-900">
      {title}
    </Text>
    {subtitle && (
      <Text className="text-xs text-typography-400">{subtitle}</Text>
    )}
  </VStack>
);

export const SsoBadge: FC = () => {
  const { i18n } = useLocalization();
  return (
    <HStack className="items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5">
      <KeyRound color="#059669" size={10} />
      <Text className="text-[11px] font-medium text-emerald-700">
        {i18n.t("accounts.ssoEnabled")}
      </Text>
    </HStack>
  );
};

export const StatusBadge: FC<{ status: AccountStatus }> = ({ status }) => {
  const labels = useStatusLabels();
  const style = STATUS_STYLE[status];
  return (
    <Box
      className="flex-row items-center gap-1.5 rounded-full px-2.5 py-1"
      style={{ backgroundColor: style.bg }}
    >
      <Box
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: style.color }}
      />
      <Text className="text-xs font-medium" style={{ color: style.color }}>
        {labels[status]}
      </Text>
    </Box>
  );
};

export const StripeBadge: FC = () => {
  const { i18n } = useLocalization();
  return (
    <HStack className="items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5">
      <CreditCard color="#4f46e5" size={10} />
      <Text className="text-[11px] font-medium text-indigo-700">
        {i18n.t("accounts.stripeLinked")}
      </Text>
    </HStack>
  );
};

// =============================================================================
// Inputs
// =============================================================================
export const CheckRow: FC<{
  checked: boolean;
  description?: string;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  title: string;
}> = ({ checked, description, disabled, onChange, title }) => (
  <Pressable
    className={`flex-row items-start gap-3 ${disabled ? "opacity-50" : ""}`}
    disabled={disabled}
    onPress={() => onChange(!checked)}
  >
    <Box
      className={`w-[18px] h-[18px] mt-0.5 rounded-md border items-center justify-center ${checked ? "bg-violet-600 border-violet-600" : "bg-white border-outline-300"}`}
    >
      {checked && <Check color="white" size={12} strokeWidth={3} />}
    </Box>
    <VStack className="flex-1 gap-0.5">
      <Text className="text-sm font-medium text-typography-900">{title}</Text>
      {description && (
        <Text className="text-xs text-typography-400 leading-5">
          {description}
        </Text>
      )}
    </VStack>
  </Pressable>
);

export const ChoiceCard: FC<{
  description?: string;
  disabled?: boolean;
  icon?: LucideIcon;
  onPress: () => void;
  selected: boolean;
  title: string;
}> = ({ description, disabled, icon: Icon, onPress, selected, title }) => (
  <Pressable
    className={`flex-1 min-w-[180px] rounded-xl border p-3.5 ${selected ? "border-violet-500 bg-violet-50" : "border-outline-200 bg-white hover:border-outline-300"} ${disabled ? "opacity-50" : ""}`}
    disabled={disabled}
    onPress={onPress}
  >
    <HStack className="items-start gap-3">
      {Icon && (
        <Box
          className={`w-8 h-8 rounded-lg items-center justify-center ${selected ? "bg-violet-600" : "bg-background-100"}`}
        >
          <Icon color={selected ? "white" : "#64748b"} size={15} />
        </Box>
      )}
      <VStack className="flex-1 gap-0.5">
        <Text
          bold
          className={`text-sm ${selected ? "text-violet-700" : "text-typography-900"}`}
        >
          {title}
        </Text>
        {description && (
          <Text className="text-xs text-typography-400 leading-5">
            {description}
          </Text>
        )}
      </VStack>
      <Box
        className={`w-4 h-4 rounded-full border-2 items-center justify-center ${selected ? "border-violet-600" : "border-outline-300"}`}
      >
        {selected && <Box className="w-2 h-2 rounded-full bg-violet-600" />}
      </Box>
    </HStack>
  </Pressable>
);

export const Field: FC<{
  children: ReactNode;
  error?: string | null;
  hint?: string;
  label: string;
  optional?: boolean;
}> = ({ children, error, hint, label, optional }) => {
  const { i18n } = useLocalization();
  return (
    <VStack className="flex-1 gap-1.5 min-w-[160px] min-h-min">
      <HStack className="items-center gap-1.5">
        <Text className="text-[13px] font-medium text-typography-700">
          {label}
        </Text>
        {optional && (
          <Text className="text-[11px] text-typography-400">
            {i18n.t("forms.optional")}
          </Text>
        )}
      </HStack>
      {children}
      {error ? (
        <Text className="text-xs text-error-600">{error}</Text>
      ) : (
        hint && (
          <Text className="text-xs text-typography-400 leading-5">{hint}</Text>
        )
      )}
    </VStack>
  );
};

export const RegionLock: FC<{ region: Region }> = ({ region }) => {
  const { i18n } = useLocalization();
  const labels = useRegionLabels();
  return (
    <HStack className="items-center gap-3 rounded-xl border border-outline-200 bg-background-50 px-3 py-2.5">
      <Image
        source={{ uri: labels[region].flagUrl }}
        style={{ borderRadius: 3, height: 15, width: 22 }}
      />
      <VStack className="flex-1">
        <Text className="text-sm font-medium text-typography-900">
          {labels[region].name}
        </Text>
        <Text className="text-xs text-typography-400">
          {i18n.t("forms.regionLockHint")}
        </Text>
      </VStack>
      <Lock color="#94a3b8" size={14} />
    </HStack>
  );
};

export function Segmented<T extends string>({
  onChange,
  options,
  value,
}: {
  onChange: (value: T) => void;
  options: { icon?: LucideIcon; label: string; value: T }[];
  value: T;
}) {
  return (
    <HStack className="items-center gap-1 bg-background-50 p-1 rounded-xl self-start">
      {options.map(({ icon: Icon, label, value: v }) => (
        <Pressable
          className={`flex-row items-center gap-1.5 px-3.5 py-1.5 rounded-lg ${value === v ? "bg-white shadow-sm" : ""}`}
          key={v}
          onPress={() => onChange(v)}
        >
          {Icon && (
            <Icon color={value === v ? "#0f172a" : "#64748b"} size={14} />
          )}
          <Text
            className={`text-sm font-medium ${value === v ? "text-typography-900" : "text-typography-400"}`}
          >
            {label}
          </Text>
        </Pressable>
      ))}
    </HStack>
  );
}

export const SwitchRow: FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  title: string;
}> = ({ checked, onChange, title }) => (
  <HStack className="items-center gap-3">
    <Switch
      activeThumbColor="#7c3aed"
      onValueChange={onChange}
      thumbColor="white"
      trackColor={{ false: "#e2e8f0", true: "#ddd6fe" }}
      value={checked}
    />
    <Text className="text-sm font-medium text-typography-900">{title}</Text>
  </HStack>
);

export const TextField: FC<{
  autoFocus?: boolean;
  invalid?: boolean;
  keyboardType?: KeyboardTypeOptions;
  onChangeText: (text: string) => void;
  onSubmitEditing?: () => void;
  placeholder?: string;
  value: string;
}> = ({
  autoFocus,
  invalid,
  keyboardType,
  onChangeText,
  onSubmitEditing,
  placeholder,
  value,
}) => (
  <Input
    className={`bg-white rounded-xl ${invalid ? "border-error-400" : "border-outline-200"}`}
    size="md"
  >
    <InputField
      autoFocus={autoFocus}
      className="text-sm"
      defaultValue={value}
      keyboardType={keyboardType}
      onChangeText={onChangeText}
      onSubmitEditing={onSubmitEditing}
      placeholder={placeholder}
    />
  </Input>
);

// =============================================================================
// Labels
// =============================================================================
export function useRoleLabels(): Record<string, string> {
  const { i18n } = useLocalization();
  return useMemo(
    () => ({
      "org:admin": i18n.t("accounts.roles.admin"),
      "org:member": i18n.t("accounts.roles.member"),
      owner: i18n.t("accounts.roles.owner"),
    }),
    [i18n]
  );
}

export function useStatusLabels(): Record<AccountStatus, string> {
  const { i18n } = useLocalization();
  return useMemo(
    () => ({
      active: i18n.t("accounts.status.active"),
      offboarded: i18n.t("accounts.status.offboarded"),
    }),
    [i18n]
  );
}
